/**
 * GlobalSearch — Campo de pesquisa global do header
 *
 * Permite pesquisar Contatos, Clientes, Veículos, Negócios e Tarefas
 * (Normal, Retenção e Jurídico) em um único campo, com:
 *  - Atalho Ctrl/Cmd + K
 *  - Debounce e mínimo de 3 caracteres (ver useGlobalSearch)
 *  - Pesquisa inteligente: ignora acentos, maiúsculas e máscaras (CPF/CNPJ/telefone)
 *  - Chips de filtro por módulo (reduz o custo da consulta no banco)
 *  - Resultados agrupados por tipo, navegáveis por teclado
 *
 * Navegação ao selecionar:
 *  - Contato  -> modal 360º do contato
 *  - Cliente  -> /tenant/crm/customers?customerId=<id>
 *  - Veículo  -> modal 360º do veículo
 *  - Negócio  -> modal premium do negócio
 *  - Tarefa   -> modal premium de detalhes da tarefa
 *
 * @version 1.10.0
 * RELEASE: 2026-08-28 v1.10.0 - Grupo/chip "Veículos" só aparece quando o módulo
 *          Veículos está habilitado no tenant (flag `vehicles_enabled`)
 * RELEASE: 2026-08-22 v1.9.0 - Correção definitiva da sobreposição: o portal do

 *          popover é suspenso enquanto um modal de detalhes estiver aberto, mas
 *          o estado lógico da pesquisa permanece ativo. Ao fechar o modal, a
 *          mesma busca reaparece sem disputar z-index, foco ou eventos do Radix.
 * RELEASE: 2026-08-22 v1.8.0 - Popover da pesquisa global fica ATRÁS do modal de
 *          detalhes quando um resultado é aberto (z-40 + pointer-events-none),
 *          permitindo ver o modal sem o popover sobreposto. Botão "Ver todos"
 *          ganha destaque azul (text-primary font-semibold + bg tint).
 * RELEASE: 2026-08-22 v1.7.0 - Botão "Ver todos" movido para a mesma linha do título de
 *          cada módulo (rodapé removido) e o popover passa a permanecer aberto ao abrir
 *          os modais de detalhes (contato/veículo/negócio/tarefa).
 * RELEASE: 2026-08-22 v1.6.0 - Chips/escopo de Tarefas passam a usar permissão de
 *          VISUALIZAÇÃO (useAvailableTaskTypes('view')): Retenção e Jurídico voltam
 *          a aparecer como grupos separados. Novos atalhos de rodapé por tipo.
 * RELEASE: 2026-08-22 v1.5.0 - Chips de filtro + grupos "Negócios" e "Tarefas"
 *          (Normal/Retenção/Jurídico) com abertura nos modais premium.
 * RELEASE: 2026-08-09 v1.4.0 - Novo grupo "Veículos" (placa/marca/modelo) abrindo o modal 360º
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  User,
  Building2,
  Car,
  Handshake,
  ListTodo,
  UserMinus,
  Scale,
} from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  useGlobalSearch,
  GLOBAL_SEARCH_MIN_CHARS,
  GLOBAL_SEARCH_ALL_TYPES,
  type GlobalSearchResult,
  type GlobalSearchType,
} from '@/hooks/useGlobalSearch';
import { useAvailableTaskTypes } from '@/hooks/useAvailableTaskTypes';
// v1.10.0: flag `vehicles_enabled` do tenant
import { useVehiclesModuleEnabled } from '@/hooks/useVehiclesModule';

import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
// v1.3.0: modal 360º reaproveitado (abre em modo Cliente ou modo Contato)
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
// v1.4.0: pesquisa de veículos abre o mesmo modal 360º da listagem de veículos
import { VehicleDetailsModalPremium } from '@/components/vehicles/modal/VehicleDetailsModalPremium';
import { VehicleTypeCell } from '@/components/vehicles/VehicleTypeCell';
import type { VehicleType } from '@/hooks/useCustomerVehicles';
// v1.5.0: modais premium de negócio e tarefa reaproveitados das listagens
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import { TaskDetailsModal } from '@/components/tasks/modal/TaskDetailsModal';

/** Rótulo e ícone de cada chip de filtro do dropdown */
const TYPE_CHIPS: Record<GlobalSearchType, { label: string; icon: typeof User }> = {
  contact: { label: 'Contatos', icon: User },
  customer: { label: 'Clientes', icon: Building2 },
  vehicle: { label: 'Veículos', icon: Car },
  deal: { label: 'Negócios', icon: Handshake },
  'task:normal': { label: 'Tarefas', icon: ListTodo },
  'task:retention': { label: 'Retenção', icon: UserMinus },
  'task:legal': { label: 'Jurídico', icon: Scale },
};

/** Formata valores monetários (negócios) em BRL */
function formatCurrencyBRL(value: unknown): string | null {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n === 0) return null;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}



/**
 * Monta a linha de detalhes do resultado (v1.2.0).
 *
 * Exibe, na ordem, apenas o que existir:
 *  - Cliente: CPF/CNPJ (com máscara)
 *  - Telefone (com máscara)
 *  - E-mail
 *  - @instagram
 *
 * Para clientes, telefone/e-mail/instagram vêm do contato principal
 * (resolvidos na RPC global_search v1.1.0).
 *
 * Fallback seguro: se nada for resolvido, devolve o `subtitle` original.
 */
function buildResultDetails(item: GlobalSearchResult): string {
  const extra = (item.extra ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);

  const parts: string[] = [];

  // Documento (somente clientes) — prioriza CNPJ, depois CPF
  if (item.entity_type === 'customer') {
    const doc = str(extra.cnpj) ?? str(extra.cpf);
    if (doc) parts.push(formatDocumentForDisplay(doc) || doc);
  }

  const phone = str(extra.phone);
  if (phone) parts.push(formatPhoneForDisplay(phone) || phone);

  const email = str(extra.email);
  if (email) parts.push(email);

  const instagram = str(extra.instagram);
  if (instagram) parts.push(`@${instagram.replace(/^@/, '')}`);

  if (parts.length === 0) return item.subtitle ?? '';

  return parts.join(' · ');
}



export function GlobalSearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  /** v1.3.0: contato selecionado para abrir o modal 360º */
  const [detailsContact, setDetailsContact] = useState<{
    id: string;
    name: string;
    phone: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null>(null);
  /** v1.4.0: veículo selecionado para abrir o modal 360º */
  const [detailsVehicleId, setDetailsVehicleId] = useState<string | null>(null);
  /** v1.5.0: negócio/tarefa selecionados para abrir os modais premium */
  const [detailsDealId, setDetailsDealId] = useState<string | null>(null);
  const [detailsTaskId, setDetailsTaskId] = useState<string | null>(null);
  /** v1.5.0: chip ativo (null = todos os módulos disponíveis) */
  const [activeType, setActiveType] = useState<GlobalSearchType | null>(null);

  // v1.6.0: tipos de tarefa habilitados no tenant e VISÍVEIS ao usuário.
  // Aqui a intenção é leitura (pesquisa), por isso 'view' e não 'create' —
  // antes o filtro exigia permissão de criação e escondia Retenção/Jurídico.
  const { types: availableTaskTypes } = useAvailableTaskTypes('view');


  // v1.10.0: módulo Veículos do tenant (flag `vehicles_enabled`)
  const vehiclesModuleEnabled = useVehiclesModuleEnabled();

  /** Chips exibidos: módulos fixos + tipos de tarefa disponíveis */
  const visibleTypes = useMemo<GlobalSearchType[]>(() => {
    const taskTypes = availableTaskTypes.map((t) => `task:${t}` as GlobalSearchType);
    return GLOBAL_SEARCH_ALL_TYPES.filter(
      (t) =>
        // v1.10.0: remove "Veículos" quando o módulo está desligado no tenant
        (t !== 'vehicle' || vehiclesModuleEnabled) &&
        (!t.startsWith('task:') || taskTypes.includes(t)),
    );
  }, [availableTaskTypes, vehiclesModuleEnabled]);


  /** Escopo efetivo enviado à RPC (chip ativo ou todos os visíveis) */
  const searchTypes = useMemo<GlobalSearchType[]>(
    () => (activeType ? [activeType] : visibleTypes),
    [activeType, visibleTypes],
  );

  const {
    contacts,
    customers,
    vehicles,
    deals,
    tasksNormal,
    tasksRetention,
    tasksLegal,
    isLoading,
    isError,
    isValidTerm,
    debouncedTerm,
    total,
  } = useGlobalSearch(term, open, searchTypes);


  // Atalho global Ctrl/Cmd + K para focar o campo
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
        // Foco após o popover abrir
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  /** Fecha o dropdown e limpa o estado após navegar (mudança de rota) */
  const closeAndReset = () => {
    setOpen(false);
    setTerm('');
  };

  /**
   * v1.7.0 — Indica se algum modal de detalhes está aberto sobre o popover.
   * Enquanto houver modal, o popover NÃO pode fechar por interação/foco externo,
   * assim o usuário volta para a mesma lista de resultados ao fechar o modal.
   */
  const hasOpenModal =
    !!detailsContact || !!detailsVehicleId || !!detailsDealId || !!detailsTaskId;

  /**
   * v1.7.0 — Navega para uma listagem completa já filtrada pelo termo pesquisado.
   * Usado pelos botões "Ver todos" ao lado do título de cada módulo.
   */
  const goToList = (path: string) => {
    navigate(`${path}?q=${encodeURIComponent(debouncedTerm)}`);
    closeAndReset();
  };

  /**
   * v1.7.0 — Cabeçalho de grupo com o nome do módulo e o atalho "Ver todos"
   * na mesma linha, evitando duplicar markup nos 7 grupos de resultado.
   */
  const renderGroupHeading = (label: string, listPath: string) => (
    <div className="flex items-center justify-between gap-2 pr-1">
      <span>{label}</span>
      <button
        type="button"
        // onMouseDown evita que o cmdk trate o clique como seleção de item
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          goToList(listPath);
        }}
        // v1.8.0: destaque azul para o "Ver todos" (mais visível no agrupamento)
        className="rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary transition-colors hover:bg-primary/20 hover:underline"
      >
        Ver todos
      </button>
    </div>
  );

  /**
   * v1.3.0 — Clique no resultado de contato abre o modal 360º (mesmo da listagem
   * de Contatos) em vez de redirecionar para a página de listagem.
   * O modal resolve internamente se existe cliente vinculado.
   * v1.7.0: mantém o popover aberto (não chama closeAndReset).
   */
  const openContactModal = (item: GlobalSearchResult) => {
    const extra = (item.extra ?? {}) as Record<string, unknown>;
    const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    setDetailsContact({
      id: item.entity_id,
      name: item.title ?? 'Contato',
      phone: str(extra.phone),
      email: str(extra.email),
      avatar_url: item.avatar_url,
    });
  };

  const goToCustomer = (customerId: string) => {
    navigate(`/tenant/crm/customers?customerId=${customerId}`);
    closeAndReset();
  };

  /**
   * v1.5.0 — Renderiza um grupo de tarefas (Normal, Retenção ou Jurídico).
   * Reaproveita o mesmo layout de item dos demais módulos e abre o modal
   * premium de detalhes da tarefa.
   */
  const renderTaskGroup = (
    heading: string,
    items: GlobalSearchResult[],
    Icon: typeof ListTodo,
    listPath: string,
  ) => {
    if (isLoading || isError || items.length === 0) return null;

    return (
      <CommandGroup heading={renderGroupHeading(heading, listPath)}>
        {items.map((t) => {
          const extra = (t.extra ?? {}) as Record<string, unknown>;
          const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
          // Identificador exibido: protocolo (retenção), nº do processo (jurídico) ou nº da tarefa
          const identifier =
            str(extra.protocol_number) ??
            str(extra.process_number) ??
            (extra.number != null ? `#${extra.number}` : null);
          const details = [t.subtitle, identifier].filter(Boolean).join(' · ');
          const phaseName = str(extra.phase_name);

          return (
            <CommandItem
              key={`task-${t.entity_id}`}
              value={`task-${t.entity_id}`}
              // v1.7.0: mantém o popover aberto ao abrir o modal
              onSelect={() => setDetailsTaskId(t.entity_id)}
              className="gap-2"
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                <Icon className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium">{t.title || 'Sem título'}</p>
                {details && (
                  <p className="truncate text-[11px] text-muted-foreground">{details}</p>
                )}
              </div>
              {phaseName && (
                <span className="shrink-0 truncate text-[11px] text-muted-foreground">
                  {phaseName}
                </span>
              )}
            </CommandItem>
          );
        })}
      </CommandGroup>
    );
  };


  return (
    <>

    <Popover
      // v1.9.0: mantém `open` como estado lógico da pesquisa, mas desmonta o
      // portal visual enquanto um modal está aberto. Assim, o Dialog não disputa
      // z-index/foco com o Popover e a busca reaparece intacta ao fechar o modal.
      open={open && !hasOpenModal}
      onOpenChange={(nextOpen) => {
        // Eventos de fechamento gerados durante a abertura do Dialog não devem
        // apagar o estado lógico que será restaurado ao retornar à pesquisa.
        if (!hasOpenModal) setOpen(nextOpen);
      }}
    >
      <PopoverTrigger asChild>
        {/* v1.1.0: campo em pílula, largo e centralizado (referência Pipedrive). */}
        <div className="relative w-full">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Pesquisar no sistema"
            className="h-10 rounded-full border-border bg-background pl-11 pr-14 text-sm shadow-sm transition-shadow placeholder:text-muted-foreground focus-visible:shadow-md focus-visible:ring-2 focus-visible:ring-primary/30"
            aria-label="Pesquisa global"
          />
          <kbd className="absolute right-3 top-1/2 hidden -translate-y-1/2 select-none rounded-md border bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground lg:inline-block">
            ⌘K
          </kbd>
        </div>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[--radix-popover-trigger-width] min-w-[320px] rounded-xl p-0"
        // Mantém o foco no input ao abrir (não rouba o cursor)
        onOpenAutoFocus={(e) => e.preventDefault()}
      >

        <Command shouldFilter={false}>
          {/* v1.5.0: chips de filtro por módulo (reduz o escopo consultado) */}
          <div className="flex flex-wrap items-center gap-1 border-b p-2">
            <Badge
              variant={activeType === null ? 'default' : 'outline'}
              className="cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium"
              onClick={() => setActiveType(null)}
            >
              Tudo
            </Badge>
            {visibleTypes.map((t) => {
              const chip = TYPE_CHIPS[t];
              const Icon = chip.icon;
              return (
                <Badge
                  key={t}
                  variant={activeType === t ? 'default' : 'outline'}
                  className="cursor-pointer gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium"
                  onClick={() => setActiveType(activeType === t ? null : t)}
                >
                  <Icon className="h-3 w-3" />
                  {chip.label}
                </Badge>
              );
            })}
          </div>

          <CommandList className="max-h-[360px]">

            {!isValidTerm && (
              <div className="px-4 py-6 text-center text-xs text-muted-foreground">
                Digite ao menos {GLOBAL_SEARCH_MIN_CHARS} caracteres para pesquisar.
              </div>
            )}

            {isValidTerm && isLoading && (
              <div className="flex items-center justify-center gap-2 px-4 py-6 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Pesquisando...
              </div>
            )}

            {isValidTerm && !isLoading && isError && (
              <div className="px-4 py-6 text-center text-xs text-destructive">
                Não foi possível concluir a pesquisa.
              </div>
            )}

            {isValidTerm && !isLoading && !isError && total === 0 && (
              <CommandEmpty className="py-6 text-xs">Nenhum resultado encontrado.</CommandEmpty>
            )}

            {!isLoading && !isError && contacts.length > 0 && (
              <CommandGroup heading={renderGroupHeading('Contatos', '/tenant/contacts')}>
                {contacts.map((c) => (
                  <CommandItem
                    key={`contact-${c.entity_id}`}
                    value={`contact-${c.entity_id}`}
                    onSelect={() => openContactModal(c)}
                    className="gap-2"
                  >
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={c.avatar_url ?? undefined} alt={c.title ?? ''} />
                      <AvatarFallback className="text-[10px]">
                        <User className="h-3 w-3" />
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{c.title || 'Sem nome'}</p>
                      {buildResultDetails(c) && (
                        <p className="truncate text-[11px] text-muted-foreground">{buildResultDetails(c)}</p>
                      )}

                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!isLoading && !isError && customers.length > 0 && (
              <CommandGroup heading={renderGroupHeading('Clientes', '/tenant/crm/customers')}>
                {customers.map((c) => (
                  <CommandItem
                    key={`customer-${c.entity_id}`}
                    value={`customer-${c.entity_id}`}
                    onSelect={() => goToCustomer(c.entity_id)}
                    className="gap-2"
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                      <Building2 className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">{c.title || 'Sem nome'}</p>
                      {buildResultDetails(c) && (
                        <p className="truncate text-[11px] text-muted-foreground">{buildResultDetails(c)}</p>
                      )}

                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {!isLoading && !isError && vehicles.length > 0 && (
              <CommandGroup heading={renderGroupHeading('Veículos', '/tenant/vehicles/list')}>
                {vehicles.map((v) => {
                  const extra = (v.extra ?? {}) as Record<string, unknown>;
                  const year = typeof extra.year === 'number' ? extra.year : null;
                  const details = [v.subtitle, year ? String(year) : null]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <CommandItem
                      key={`vehicle-${v.entity_id}`}
                      value={`vehicle-${v.entity_id}`}
                      // v1.7.0: mantém o popover aberto ao abrir o modal
                      onSelect={() => setDetailsVehicleId(v.entity_id)}

                      className="gap-2"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                        <Car className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-mono text-xs font-medium">
                          {v.title || 'Sem placa'}
                        </p>
                        {details && (
                          <p className="truncate text-[11px] text-muted-foreground">{details}</p>
                        )}
                      </div>
                      <VehicleTypeCell
                        type={(extra.vehicle_type as VehicleType) ?? 'OTHER'}
                        categoryName={(extra.category_name as string) ?? null}
                        className="shrink-0 text-[11px] text-muted-foreground"
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* v1.5.0: Negócios */}
            {!isLoading && !isError && deals.length > 0 && (
              <CommandGroup heading={renderGroupHeading('Negócios', '/tenant/crm/deals')}>
                {deals.map((d) => {
                  const extra = (d.extra ?? {}) as Record<string, unknown>;
                  const value = formatCurrencyBRL(extra.value);
                  const details = [d.subtitle, value].filter(Boolean).join(' · ');
                  const phaseName = typeof extra.phase_name === 'string' ? extra.phase_name : null;
                  return (
                    <CommandItem
                      key={`deal-${d.entity_id}`}
                      value={`deal-${d.entity_id}`}
                      // v1.7.0: mantém o popover aberto ao abrir o modal
                      onSelect={() => setDetailsDealId(d.entity_id)}

                      className="gap-2"
                    >
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted">
                        <Handshake className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-xs font-medium">{d.title || 'Sem título'}</p>
                        {details && (
                          <p className="truncate text-[11px] text-muted-foreground">{details}</p>
                        )}
                      </div>
                      {phaseName && (
                        <span className="shrink-0 truncate text-[11px] text-muted-foreground">
                          {phaseName}
                        </span>
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {/* v1.5.0: Tarefas por tipo (Normal / Retenção / Jurídico) */}
            {renderTaskGroup('Tarefas', tasksNormal, ListTodo, '/tenant/tasks/list')}
            {renderTaskGroup('Retenção', tasksRetention, UserMinus, '/tenant/retention/list')}
            {renderTaskGroup('Jurídico', tasksLegal, Scale, '/tenant/legal/list')}
          </CommandList>


        </Command>
      </PopoverContent>
    </Popover>

    {/* v1.3.0: modal 360º do contato selecionado na pesquisa */}
    <CustomerDetailsModalPremium
      customerId={null}
      contactId={detailsContact?.id ?? null}
      contactPreview={detailsContact}
      open={!!detailsContact}
      onOpenChange={(o) => { if (!o) setDetailsContact(null); }}
    />

    {/* v1.4.0: modal 360º do veículo selecionado na pesquisa */}
    {detailsVehicleId && (
      <VehicleDetailsModalPremium
        vehicleId={detailsVehicleId}
        open={!!detailsVehicleId}
        onOpenChange={(o) => { if (!o) setDetailsVehicleId(null); }}
      />
    )}

    {/* v1.5.0: modal premium do negócio selecionado na pesquisa */}
    {detailsDealId && (
      <DealDetailsModalPremium
        dealId={detailsDealId}
        open={!!detailsDealId}
        onOpenChange={(o) => { if (!o) setDetailsDealId(null); }}
      />
    )}

    {/* v1.5.0: modal premium da tarefa selecionada na pesquisa */}
    {detailsTaskId && (
      <TaskDetailsModal
        taskId={detailsTaskId}
        open={!!detailsTaskId}
        onOpenChange={(o) => { if (!o) setDetailsTaskId(null); }}
        onNavigateToTask={(id) => setDetailsTaskId(id)}
      />
    )}
    </>

  );
}

